import React, { useState, useEffect, useRef } from 'react';
import Carousel from 'react-bootstrap/Carousel';
import '../styles/home.css';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import WorkProcess from './WorkProcess';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  FaCalendarCheck, FaClock, FaLock, FaLeaf,
  FaTrophy, FaShieldAlt, FaArrowRight,
  FaLaptopCode, FaIndustry, FaCheckCircle,
  FaTruck, FaCogs, FaRecycle, FaQuoteLeft,
  FaStar, FaPlay,
  FaChevronLeft, FaChevronRight, FaPhoneAlt
} from 'react-icons/fa';

/* ─── Testimonials ─── */
const testimonials = [
  {
    id: 1,
    text: "KMK helped us dispose of over 200 machines securely. Their team was professional, punctual, and gave us full documentation for every device. Absolutely reliable.",
    author: "Rajesh Mehta",
    role: "IT Manager, TechCorp India",
    rating: 5,
  },
  {
    id: 2,
    text: "We needed a certified partner for bulk metal scrap. KMK exceeded expectations — fast pickup, fair valuation, and proper recycling certification. Highly recommended.",
    author: "Priya Sharma",
    role: "Operations Head, MetaSteel Ltd",
    rating: 5,
  },
  {
    id: 3,
    text: "Data security was our top concern. KMK's destruction process gave us complete peace of mind. We've been partnering with them for 3 years now.",
    author: "Ankit Verma",
    role: "CTO, FinServe Solutions",
    rating: 5,
  },
];

/* ─── Framer variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 48 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const fadeLeft = {
  hidden: { opacity: 0, x: 60 },
  show:   { opacity: 1, x: 0,  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const fadeRight = {
  hidden: { opacity: 0, x: -60 },
  show:   { opacity: 1, x: 0,  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.11, delayChildren: 0.1 } },
};
const heroItem = {
  hidden: { opacity: 0, y: 36 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

/* ─── Count-up hook ─── */
function useCountUp(end, duration = 1.8) {
  const [count, setCount] = useState(0);
  const ref  = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  useEffect(() => {
    if (!inView) return;
    let startTs = null;
    const tick = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / (duration * 1000), 1);
      const ease = 1 - Math.pow(1 - p, 3);      // ease-out-cubic
      setCount(Math.floor(ease * end));
      if (p < 1) requestAnimationFrame(tick);
      else setCount(end);
    };
    requestAnimationFrame(tick);
  }, [inView, end, duration]);
  return [count, ref];
}

/* ─── Section wrapper that animates in on scroll ─── */
function RevealSection({ children, className = '', id = '', delay = 0 }) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      id={id}
      className={className}
      ref={ref}
      variants={stagger}
      initial="hidden"
      animate={inView ? 'show' : 'hidden'}
      style={{ '--reveal-delay': `${delay}s` }}
    >
      {children}
    </motion.section>
  );
}

/* ══════════════════════════════════════════════════════════
   COMPONENT
   ══════════════════════════════════════════════════════════ */
const Home = () => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [clientsCount, clientsRef] = useCountUp(500);
  const [yearsCount,   yearsRef  ] = useCountUp(10);
  const [projCount,    projRef   ] = useCountUp(1200);

  // Auto-advance testimonials
  useEffect(() => {
    const id = setInterval(() => {
      setCurrentIndex(p => (p + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const goToNext = () => setCurrentIndex(p => (p + 1) % testimonials.length);
  const goToPrev = () => setCurrentIndex(p => (p === 0 ? testimonials.length - 1 : p - 1));

  const slickSettings = {
    dots: false, infinite: true, speed: 700,
    slidesToShow: 5, slidesToScroll: 1,
    autoplay: true, autoplaySpeed: 1800, arrows: false,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 4 } },
      { breakpoint: 768,  settings: { slidesToShow: 3 } },
      { breakpoint: 480,  settings: { slidesToShow: 2 } },
    ],
  };

  const heroSlides = [
    {
      bg: '/Assets/kmkimg13.jpg',
      badge: 'Reduce · Reuse · Recycle',
      title: 'Responsible Recycling\nfor a Cleaner Tomorrow',
      sub: 'E-waste & metal recycling — certified, secure, eco-friendly.',
      cta1: { label: 'Get Started', path: '/contact' },
      cta2: { label: 'Learn More',  path: '/about'   },
    },
    {
      bg: '/Assets/kmkimg7.jpg',
      badge: 'Welcome to KMK Industries',
      title: 'Complete IT Asset\nDisposition Services',
      sub: 'Secure data destruction & maximum asset value recovery.',
      cta1: { label: 'Our Services', path: '/services' },
      cta2: { label: 'View Gallery', path: '/galary'   },
    },
    {
      bg: '/Assets/kmkimg3.jpg',
      badge: 'Certified & Trusted',
      title: 'Your Data is Safe\nWith Us',
      sub: 'Industry-grade data erasure before every single disposal.',
      cta1: { label: 'Contact Us',  path: '/contact'  },
      cta2: { label: 'Know More',   path: '/services' },
    },
  ];

  return (
    <div className="home-root">

      {/* ════════════════════════════════════════
          HERO
          ════════════════════════════════════════ */}
      <section id="hero">
        <Carousel fade interval={4500} controls={false} indicators
          onSlide={i => setActiveSlide(i)}>
          {heroSlides.map((slide, idx) => (
            <Carousel.Item key={idx}>
              <div className="heroSlide" style={{ backgroundImage: `url('${slide.bg}')` }}>
                <div className="heroOverlay" />
                <div className="heroParticles" aria-hidden="true">
                  {[...Array(6)].map((_, i) => (
                    <span key={i} className={`heroPart heroPart--${i + 1}`} />
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  {activeSlide === idx && (
                    <motion.div
                      key={`hero-${idx}`}
                      className="heroContent"
                      variants={stagger}
                      initial="hidden"
                      animate="show"
                      exit={{ opacity: 0, transition: { duration: 0.3 } }}
                    >
                      <motion.span className="heroBadge" variants={heroItem}>
                        {slide.badge}
                      </motion.span>

                      <motion.h1 className="heroTitle" variants={heroItem}>
                        {slide.title.split('\n').map((l, i) => (
                          <span key={i} className={i === 1 ? 'heroTitleAccent' : ''}>{l}<br /></span>
                        ))}
                      </motion.h1>

                      <motion.p className="heroSub" variants={heroItem}>
                        {slide.sub}
                      </motion.p>

                      <motion.div className="heroBtns" variants={heroItem}>
                        <button className="heroBtnPrimary" onClick={() => navigate(slide.cta1.path)}>
                          {slide.cta1.label} <FaArrowRight className="btnIcon" />
                        </button>
                        <button className="heroBtnOutline" onClick={() => navigate(slide.cta2.path)}>
                          {slide.cta2.label}
                        </button>
                      </motion.div>

                      <motion.div className="heroTrustRow" variants={heroItem}>
                        <span className="heroTrustItem"><FaCheckCircle /> ISO Certified</span>
                        <span className="heroTrustItem"><FaShieldAlt /> 100% Secure</span>
                        <span className="heroTrustItem"><FaLeaf /> Eco Friendly</span>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Floating stat card */}
                <motion.div
                  className="heroStatCard"
                  initial={{ opacity: 0, x: 60, y: 20 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.7, ease: 'easeOut' }}
                >
                  <span className="heroStatNum">500+</span>
                  <span className="heroStatLbl">Happy Clients</span>
                </motion.div>
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      </section>

      {/* ════════════════════════════════════════
          STATS BAR
          ════════════════════════════════════════ */}
      <div className="statsBar" ref={clientsRef}>
        <div className="container statsBarInner">
          {[
            { numRef: null, val: clientsCount, suffix: '+', label: 'Clients Served' },
            { numRef: yearsRef, val: yearsCount, suffix: '+', label: 'Years Experience' },
            { numRef: projRef,  val: projCount, suffix: '+', label: 'Assets Processed' },
            { numRef: null, val: 100, suffix: '%', label: 'Data Security' },
          ].map((s, i) => (
            <div className="statsBarItem" key={i} ref={s.numRef}>
              <span className="statsBarNum">{s.val}{s.suffix}</span>
              <span className="statsBarLbl">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ════════════════════════════════════════
          PIONEERS / ABOUT INTRO
          ════════════════════════════════════════ */}
      <RevealSection id="pioneers" className="section-pioneers">
        <div className="container">
          <div className="pioneers-inner">

            <motion.div className="pioneers-text" variants={fadeRight}>
              <p className="section-eyebrow">Waste Disposal &amp; Recycling Services</p>
              <h2 className="section-title-lg">The Pioneers In<br /><span className="text-green">Recycling Services</span></h2>
              <p className="section-body">
                We deliver certified e-waste &amp; metal recycling backed by modern technology.
                Our client-first approach ensures seamless pickup, secure data destruction,
                and maximum value recovery for every asset.
              </p>

              <div className="feature-list">
                {[
                  { icon: <FaCalendarCheck />, label: 'Scheduled Pickup',  desc: 'At your convenience, always on time.' },
                  { icon: <FaClock />,         label: '24 / 7 Support',    desc: 'Round-the-clock assistance.' },
                  { icon: <FaLock />,          label: 'Secure Data Wipe',  desc: 'Certified erasure before disposal.' },
                  { icon: <FaLeaf />,          label: 'Eco Certified',     desc: 'Responsible recycling methods.' },
                ].map((f, i) => (
                  <motion.div
                    className="feature-row"
                    key={i}
                    variants={fadeUp}
                    custom={i}
                  >
                    <span className="feature-dot">{f.icon}</span>
                    <div>
                      <strong>{f.label}</strong>
                      <p>{f.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="pioneers-actions">
                <button className="btn-primary-green" onClick={() => navigate('/contact')}>
                  Get Free Pickup Quote <FaArrowRight />
                </button>
                <a href="tel:+918762414394" className="btn-call">
                  <span className="btn-call-icon"><FaPhoneAlt /></span>
                  <div>
                    <span className="btn-call-label">Call Us</span>
                    <span className="btn-call-num">+91 87624 14394</span>
                  </div>
                </a>
              </div>
            </motion.div>

            <motion.div className="pioneers-visual" variants={fadeLeft}>
              <div className="pioneers-img-wrap">
                <img src="/Assets/kmkimg3.jpg" alt="KMK Recycling Facility" className="pioneers-img" />
                <div className="pioneers-glow" />
                <motion.div
                  className="pioneers-badge"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, duration: 0.5, type: 'spring', stiffness: 120 }}
                >
                  <FaTrophy className="pb-icon" />
                  <div>
                    <strong>Trusted Partner</strong>
                    <p>ISO Certified Recycling</p>
                  </div>
                </motion.div>
                <motion.div
                  className="pioneers-badge2"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7, duration: 0.5, type: 'spring', stiffness: 120 }}
                >
                  <FaRecycle className="pb2-icon" />
                  <div>
                    <strong>10+ Years</strong>
                    <p>of Excellence</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>

          </div>
        </div>
      </RevealSection>

      {/* ════════════════════════════════════════
          SERVICES
          ════════════════════════════════════════ */}
      <RevealSection id="ourservices" className="section-services">
        <span className="svc-glow-a" aria-hidden="true" />
        <span className="svc-glow-b" aria-hidden="true" />
        <div className="container">
          <motion.div className="sec-header" variants={fadeUp}>
            <p className="section-eyebrow">What We Offer</p>
            <h2 className="section-title-lg">Our <span className="text-green">Services</span></h2>
            <p className="section-body muted center">
              End-to-end recycling solutions for IT assets and all metals — secure, certified, eco-friendly.
            </p>
          </motion.div>

          <div className="services-grid">
            {[
              {
                img: '/Assets/kmkimg16.jpg',
                tag: <><FaLaptopCode /> IT Assets</>,
                title: 'IT Assets Disposition',
                desc: 'Secure pickup, certified data destruction, and maximum value recovery for all IT equipment — laptops, servers, desktops and more.',
                points: ['Servers, Laptops & Desktops', 'Certified Data Erasure', 'Asset Value Recovery'],
                href: '/services',
              },
              {
                img: '/Assets/imgkmk13.jpg',
                tag: <><FaIndustry /> All Metals</>,
                title: 'All Metal Disposition',
                desc: 'Your trusted partner for ferrous and non-ferrous metals. Responsible recycling practices with bulk and commercial pickup options.',
                points: ['Ferrous & Non-Ferrous Metals', 'Bulk & Commercial Pickup', 'Eco-Certified Processing'],
                href: '/allmetal',
              },
            ].map((c, i) => (
              <motion.div
                className="svc-card"
                key={i}
                variants={fadeUp}
                custom={i}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <div className="svc-img-wrap">
                  <img src={c.img} alt={c.title} loading="lazy" />
                  <span className="svc-tag">{c.tag}</span>
                  <div className="svc-img-shade" />
                </div>
                <div className="svc-body">
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                  <ul className="svc-checklist">
                    {c.points.map((pt, j) => (
                      <li key={j}><FaCheckCircle /> {pt}</li>
                    ))}
                  </ul>
                  <a href={c.href} className="btn-link-arrow">
                    Learn More <FaArrowRight />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ════════════════════════════════════════
          RECYCLING PROCESS (4-step visual)
          ════════════════════════════════════════ */}
      <RevealSection id="recycling" className="section-recycling">
        <div className="container">
          <div className="recycling-intro">
            <motion.div className="recycling-intro-text" variants={fadeRight}>
              <p className="section-eyebrow">Recycling Wastage · Save Environment</p>
              <h2 className="section-title-lg">From Waste to Resource —<br /><span className="text-green">Our Simple Process</span></h2>
              <p className="section-body">
                Recycling converts waste into reusable materials using modern machinery
                and skilled staff. Every item is handled responsibly from pickup to final processing.
              </p>
              <button className="btn-primary-green" onClick={() => navigate('/contact')}>
                Get Started <FaArrowRight />
              </button>
            </motion.div>
            <motion.div className="recycling-intro-img" variants={fadeLeft}>
              <img src="/Assets/kmkimg10.jpg" alt="Recycling process" loading="lazy" />
              <div className="ri-glow" />
            </motion.div>
          </div>

          <div className="process-track">
            {[
              { num: '01', icon: <FaTruck />,     title: 'Collection',  text: 'We arrange pickup of e-waste and metal scrap directly from your site.' },
              { num: '02', icon: <FaShieldAlt />, title: 'Security',    text: 'Safe, secure transportation to our certified facilities.' },
              { num: '03', icon: <FaCogs />,      title: 'Processing',  text: 'Materials sorted by type and condition at our plant.' },
              { num: '04', icon: <FaRecycle />,   title: 'Recycling',   text: 'Eco-friendly recycling methods for all non-reusable items.' },
            ].map((step, i) => (
              <motion.div
                className="process-step"
                key={i}
                variants={fadeUp}
                custom={i}
              >
                <div className="ps-connector">
                  <div className="ps-dot" />
                  {i < 3 && <div className="ps-line" />}
                </div>
                <div className="ps-card">
                  <span className="ps-num">{step.num}</span>
                  <div className="ps-icon">{step.icon}</div>
                  <h5 className="ps-title">{step.title}</h5>
                  <p className="ps-text">{step.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ════════════════════════════════════════
          WORK PROCESS
          ════════════════════════════════════════ */}
      <WorkProcess />

      {/* ════════════════════════════════════════
          WHY CHOOSE KMK
          ════════════════════════════════════════ */}
      <RevealSection id="choose" className="section-choose">
        <div className="choose-glow" aria-hidden="true" />
        <div className="container">
          <div className="choose-inner">

            <motion.div className="choose-visual" variants={fadeRight}>
              <div className="choose-img-wrap">
                <img src="/Assets/imgkmk2.jpg" alt="KMK Industries" loading="lazy" className="choose-img" />
                <div className="choose-img-overlay" />
              </div>
              <motion.div
                className="choose-badge"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.5, type: 'spring' }}
              >
                <span className="cb-num">10+</span>
                <span className="cb-lbl">Years of Excellence</span>
              </motion.div>
            </motion.div>

            <motion.div className="choose-content" variants={fadeLeft}>
              <p className="section-eyebrow">Why Choose Us</p>
              <h2 className="section-title-lg">KMK Industries —<br /><span className="text-green">Built on Trust &amp; Responsibility</span></h2>
              <p className="section-body">
                Decades of expertise combined with sustainable practices make us the
                most reliable partner for your recycling needs.
              </p>
              <ul className="choose-list">
                {[
                  { icon: <FaShieldAlt />, title: 'Expertise & Experience',  desc: 'Hands-on experience with all types of e-waste and metals at scale.' },
                  { icon: <FaLock />,      title: 'Secure Data Destruction', desc: 'Certified erasure — zero compromise on your data.' },
                  { icon: <FaCogs />,      title: 'Customized Solutions',    desc: 'Flexible programmes for small businesses and large enterprises.' },
                  { icon: <FaTruck />,     title: 'Efficient Logistics',     desc: 'Direct collection from your premises — completely hassle-free.' },
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    variants={fadeUp}
                    custom={i}
                    whileHover={{ x: 6, transition: { duration: 0.2 } }}
                  >
                    <span className="choose-icon">{item.icon}</span>
                    <div>
                      <h6>{item.title}</h6>
                      <p>{item.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
              <button className="btn-primary-green" onClick={() => navigate('/about')}>
                Discover Our Story <FaArrowRight />
              </button>
            </motion.div>

          </div>
        </div>
      </RevealSection>

      {/* ════════════════════════════════════════
          OUR CLIENTS
          ════════════════════════════════════════ */}
      <section id="ourclients" className="section-clients">
        {/* Background decoration */}
        <div className="clients-bg-decor" aria-hidden="true" />

        <div className="container">
          {/* Header */}
          <motion.div
            className="clients-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <span className="clients-eyebrow">
              <span className="clients-eyebrow-line" />
              Trusted By
              <span className="clients-eyebrow-line" />
            </span>
            <h2 className="clients-title">
              Our <span className="text-green">Clients</span>
            </h2>
            <p className="clients-subtitle">
              Trusted by leading businesses across India for certified, reliable recycling.
            </p>
          </motion.div>
        </div>

        {/* Infinite marquee track — full width, outside container */}
        <div className="clients-marquee-wrap">
          <div className="clients-marquee">
            {/* Duplicate the list for seamless loop */}
            {[...Array(2)].map((_, pass) => (
              <div className="clients-track" key={pass} aria-hidden={pass === 1}>
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <div className="client-card" key={`${pass}-${n}`}>
                    <img
                      src={`/Assets/client${n}logo.png`}
                      alt={`Client ${n}`}
                      className="client-logo"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Stat strip below logos */}
        <div className="container">
          <motion.div
            className="clients-stat-strip"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          >
            {[
              { num: '500+', label: 'Happy Clients' },
              { num: '10+',  label: 'Years of Trust' },
              { num: '100%', label: 'Satisfaction Rate' },
            ].map((s, i) => (
              <div className="css-stat" key={i}>
                <span className="css-stat-num">{s.num}</span>
                <span className="css-stat-lbl">{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          TESTIMONIALS
          ════════════════════════════════════════ */}
      <RevealSection id="testimonials" className="section-testi">
        <div className="testi-bg-decor" aria-hidden="true" />
        <div className="container">
          <motion.div className="sec-header" variants={fadeUp}>
            <p className="section-eyebrow">What They Say</p>
            <h2 className="section-title-lg">Customer <span className="text-green">Stories</span></h2>
          </motion.div>

          <motion.div className="testi-wrap" variants={fadeUp}>
            <button className="testi-arrow" onClick={goToPrev} aria-label="Previous testimonial">
              <FaChevronLeft />
            </button>

            <div className="testi-stage">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  className="testi-card"
                  initial={{ opacity: 0, y: 30, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0,  scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.96 }}
                  transition={{ duration: 0.42, ease: 'easeOut' }}
                >
                  <div className="testi-quote-ring">
                    <FaQuoteLeft className="testi-quote-icon" />
                  </div>
                  <p className="testi-text">"{testimonials[currentIndex].text}"</p>
                  <div className="testi-stars">
                    {[...Array(testimonials[currentIndex].rating)].map((_, j) => (
                      <FaStar key={j} />
                    ))}
                  </div>
                  <div className="testi-author">
                    <div className="testi-avatar">
                      {testimonials[currentIndex].author.charAt(0)}
                    </div>
                    <div>
                      <strong>{testimonials[currentIndex].author}</strong>
                      <p>{testimonials[currentIndex].role}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <button className="testi-arrow" onClick={goToNext} aria-label="Next testimonial">
              <FaChevronRight />
            </button>
          </motion.div>

          <div className="testi-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                className={`testi-dot${i === currentIndex ? ' active' : ''}`}
                onClick={() => setCurrentIndex(i)}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ════════════════════════════════════════
          CTA BANNER
          ════════════════════════════════════════ */}
      <RevealSection id="cta-banner" className="section-cta">
        <div className="cta-bg" aria-hidden="true" />
        <div className="cta-grid-lines" aria-hidden="true" />
        <div className="container">
          <motion.div className="cta-inner" variants={fadeUp}>
            <div className="cta-text">
              <motion.span
                className="cta-icon"
                animate={{ rotate: [0, 10, -10, 8, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                <FaRecycle />
              </motion.span>
              <h2>Ready to Recycle <span className="text-green">Responsibly?</span></h2>
              <p>Get a free quote for your e-waste or metal scrap disposal. Our team responds within 24 hours.</p>
              <div className="cta-btns">
                <button className="heroBtnPrimary" onClick={() => navigate('/contact')}>
                  Get Free Quote <FaArrowRight />
                </button>
                <button className="heroBtnOutline cta-outline" onClick={() => navigate('/services')}>
                  Explore Services
                </button>
              </div>
              <div className="cta-trust">
                <span><FaCheckCircle /> No obligation</span>
                <span><FaShieldAlt /> 100% secure</span>
                <span><FaClock /> Quick response</span>
              </div>
            </div>
          </motion.div>
        </div>
      </RevealSection>

    </div>
  );
};

export default Home;
